import type { UserEligibility } from '@wizbii/utils/models';

import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environment';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  CheckUserEligibility,
  CreateBankingUserIfEligible,
  CreateBankingUserIfEligibleSuccess,
  InitBankingStateSuccess,
  UpdateBankingUser,
} from '@stores/banking/banking-ngrx.action';
import { user } from '@stores/banking/banking-ngrx.selector';
import { secureSpaceEnabled } from '@stores/features-flags/features-flags-ngrx.selector';
import { isLogged, userId } from '@wizbii-utils/angular/stores';
import { BankingPartnerWebservice, BankingUserWebservice } from '@wizbii-utils/angular/webservices';
import { catchError, combineLatest, filter, map, of, shareReplay, switchMap, tap } from 'rxjs';

const DefaultUserEligibility: UserEligibility = { isEligible: false, ineligibilityCauses: [] };
export const initBankingState = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    bankingUserWebservice = inject(BankingUserWebservice),
    bankingPartnerWebservice = inject(BankingPartnerWebservice)
  ) =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() => combineLatest([store.select(isLogged), store.select(secureSpaceEnabled), store.select(userId)])),
      filter((userInfo): userInfo is [boolean, boolean, string] => {
        const [isLogged, secureSpaceEnabled, userId] = userInfo;
        return secureSpaceEnabled !== undefined && isLogged !== undefined && !!userId;
      }),
      switchMap(([isLogged, secureSpaceEnabled, userId]) =>
        isLogged && secureSpaceEnabled
          ? combineLatest([
              bankingUserWebservice.checkUserEligibilityV2(userId).pipe(
                shareReplay({ bufferSize: 1, refCount: true }),
                catchError(() => of(null))
              ),
              bankingUserWebservice.get(userId).pipe(
                shareReplay({ bufferSize: 1, refCount: true }),
                catchError(() => of(null)),
                switchMap((user) =>
                  user !== null
                    ? combineLatest([
                        of(user),
                        bankingUserWebservice.getBankAccount(user.userId).pipe(catchError(() => of(null))),
                        bankingPartnerWebservice.getPartner(user.trackInfo.partnerId).pipe(
                          catchError(() => of(null)),
                          shareReplay({ bufferSize: 1, refCount: true })
                        ),
                      ])
                    : of([null, null, null])
                )
              ),
            ])
          : combineLatest([of(DefaultUserEligibility), of([null, null, null])])
      ),
      map(([eligibility, [user, bankAccount, bankingPartner]]) =>
        InitBankingStateSuccess({
          eligibility,
          user,
          bankAccount,
          bankingPartner,
        })
      )
    ),
  { functional: true }
);

export const CheckUserEligibilityEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store), bankingUserWebservice = inject(BankingUserWebservice)) =>
    actions$.pipe(
      ofType(CreateBankingUserIfEligible),
      switchMap(() => combineLatest([store.select(isLogged), store.select(secureSpaceEnabled)])),
      filter(([isLogged, secureSpaceEnabled]) => secureSpaceEnabled !== undefined && isLogged !== undefined),
      switchMap(([isLogged, secureSpaceEnabled]) =>
        isLogged && secureSpaceEnabled
          ? store.select(userId).pipe(
              filter((userId): userId is string => !!userId),
              switchMap((userId) =>
                bankingUserWebservice
                  .checkUserEligibilityV2(userId)
                  .pipe(shareReplay({ bufferSize: 1, refCount: true }))
              )
            )
          : of(DefaultUserEligibility)
      ),
      map((eligibility) => CheckUserEligibility({ eligibility }))
    ),
  { functional: true }
);

export const CreateBankingUserIfEligibleEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store), bankingUserWebservice = inject(BankingUserWebservice)) =>
    actions$.pipe(
      ofType(CheckUserEligibility),
      switchMap(({ eligibility }) => {
        return combineLatest([of(eligibility.isEligible), store.select(user)]);
      }),
      filter(([isEligible, user]) => isEligible && user === null),
      switchMap(() =>
        store.select(userId).pipe(
          filter((userId): userId is string => !!userId),
          switchMap((userId: string) =>
            bankingUserWebservice.update(
              userId,
              { userId },
              {
                params: {
                  country: environment.i18n.country,
                },
              }
            )
          )
        )
      ),
      map((user) => CreateBankingUserIfEligibleSuccess({ user }))
    ),
  { functional: true }
);

export const RedirectEffect = createEffect(
  (actions$ = inject(Actions), router = inject(Router), route = inject(ActivatedRoute)) =>
    actions$.pipe(
      ofType(UpdateBankingUser),
      tap(({ redirectUrl, redirectOptions }) => {
        if (redirectUrl) {
          router.navigate(redirectUrl, {
            relativeTo: route.parent,
            ...redirectOptions,
          });
        }
      })
    ),
  { functional: true, dispatch: false }
);
